// Выбор места для плавающей кнопки-репетитора (.tk-fab, tutor-fab.tsx).
// Чистая логика без DOM: компонент собирает прямоугольники помех и зовёт
// fabCandidates + chooseFabSpot. Вынесено ради тестов (тикет 82).
//
// Помехи трёх сортов:
// - task: элементы задания (варианты, поле сборки, поле ввода, клавиши,
//   "Дальше", "Проверить", "Начать проверку", блоки с data-fab-avoid).
//   Их шар не закрывает, если на экране есть хоть одно место без них;
// - control: прочие ссылки и кнопки, шапка;
// - soft: карточки-подложки и строки текста. Накрыть их хуже, чем пустое
//   место, но лучше, чем кнопку.
//
// До тикета 82 все помехи складывались в одну площадь: шар на поле сборки
// с пропуском весил столько же, сколько шар на краю карточки или на ссылке,
// и на маленьком экране (где свободного места нет) побеждало поле сборки.
// Теперь сначала выбираются места с наименьшим перекрытием задания, и уже
// среди них работает прежнее правило площадей.

export type Box = { left: number; top: number; right: number; bottom: number }
export type ObstacleKind = 'task' | 'control' | 'soft'
// pad - запас вокруг элемента, уже прибавленный к прямоугольнику (FAB_GAP).
// Площадь считается с запасом, а перекрытие задания - по самому элементу:
// иначе шар в свободной полосе под кнопкой "Дальше" (отступ шага 112 px)
// касался бы запаса и считался стоящим на задании.
export type Obstacle = Box & { kind: ObstacleKind; weight: number; pad?: number }
export type Side = 'left' | 'right'
export type Spot = { left: number; top: number; side: Side }

export const FAB_MARGIN = 16
// Отступ вокруг помехи: шар не прилипает к кнопке вплотную.
export const FAB_GAP = 10
// Под шапкой: верхние места начинаются ниже.
export const FAB_TOP_RESERVE = 56

// Веса площадей внутри одного сорта.
// Перекрыть поле ввода хуже, чем кнопку: на телефоне шар закрывал правую
// часть поля ответа в уроке и тренажёре (QA 2026-09-24).
export const INPUT_WEIGHT = 8
// Карточка-контейнер сама по себе не кнопка (каталог грамматики, 2026-10-03).
export const CARD_WEIGHT = 0.25
// Строка текста: шар на словах мешает читать сильнее, чем на пустом краю
// карточки (/grammar/oshibki: шар вставал на вступительный текст, потому что
// текст помехой не считался вовсе, тикет 82).
export const TEXT_WEIGHT = 0.6
// Плитка-ссылка каталога (/grammar, /games, дашборд) и строка справочника
// (/review/grammar) на телефоне во всю ширину: экран из таких плиток, места
// без ссылки нет. Широкую ссылку считаем карточкой-подложкой, а строки текста
// на ней отдельно: шар встаёт на пустой край плитки, а не на подпись (тикет 89).
export const WIDE_LINK_SHARE = 2.5

export function isWideLink(width: number, fabWidth: number): boolean {
  return width >= fabWidth * WIDE_LINK_SHARE
}

export function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left)
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  return w > 0 && h > 0 ? w * h : 0
}

// Телефон: экран по третям. 0 - нижняя (ближе к большому пальцу),
// 1 - верхняя (под шапкой), 2 - средняя (там само задание и текст).
export function zoneOf(top: number, h: number, vh: number, phone: boolean): 0 | 1 | 2 {
  if (!phone) return 0
  const center = top + h / 2
  return center >= (vh * 2) / 3 ? 0 : center <= vh / 3 ? 1 : 2
}

export type CandidateInput = {
  vw: number
  vh: number
  w: number
  h: number
  // Родное место из CSS (замер без инлайн-координат).
  home: { left: number; top: number }
  // Расстояние от низа экрана до низа шара в родном месте (safe-area).
  bottomSafe: number
  phone: boolean
}

// Кандидаты по порядку предпочтения; первый - родное место.
// Телефон: вдоль правого края с шагом в полкнопки, нижняя треть снизу вверх,
// верхняя сверху вниз; потом то же у левого края; в самом конце средняя треть.
// Средняя треть - только запасной вариант (см. chooseFabSpot): владелец
// 2026-10-02 и 2026-10-03 не хотел шар посреди текста, но шар на варианте
// ответа или кнопке "Дальше" ещё хуже (тикет 82).
export function fabCandidates({ vw, vh, w, h, home, bottomSafe, phone }: CandidateInput): Spot[] {
  const right = vw - w - FAB_MARGIN
  const left = FAB_MARGIN
  const homeSpot: Spot = { left: home.left, top: home.top, side: home.left + w / 2 < vw / 2 ? 'left' : 'right' }
  if (!phone) {
    return [
      homeSpot,
      { left: right, top: vh - h - bottomSafe, side: 'right' },
      { left, top: vh - h - bottomSafe, side: 'left' },
      { left: right, top: (vh - h) / 2, side: 'right' },
      { left, top: (vh - h) / 2, side: 'left' },
      { left: right, top: FAB_MARGIN + FAB_TOP_RESERVE, side: 'right' },
      { left, top: FAB_MARGIN + FAB_TOP_RESERVE, side: 'left' },
    ]
  }
  const tops: number[] = []
  for (let top = vh - h - bottomSafe; top >= FAB_MARGIN + FAB_TOP_RESERVE; top -= Math.max(24, h / 2)) tops.push(top)
  const low = tops.filter((t) => zoneOf(t, h, vh, true) === 0)
  const high = tops.filter((t) => zoneOf(t, h, vh, true) === 1).reverse()
  const mid = tops.filter((t) => zoneOf(t, h, vh, true) === 2)
  const along = (ts: number[], side: Side): Spot[] => ts.map((top) => ({ left: side === 'right' ? right : left, top, side }))
  return [homeSpot, ...along(low, 'right'), ...along(high, 'right'), ...along(low, 'left'), ...along(high, 'left'), ...along(mid, 'right'), ...along(mid, 'left')]
}

export type SpotScore = { task: number; area: number; hard: number }

const GAP_SHARE = 0.1

export function scoreSpot(spot: { left: number; top: number }, w: number, h: number, obstacles: Obstacle[]): SpotScore {
  const box: Box = { left: spot.left, top: spot.top, right: spot.left + w, bottom: spot.top + h }
  let task = 0
  let area = 0
  let hard = 0
  for (const o of obstacles) {
    const a = overlapArea(box, o)
    if (!a) continue
    const p = o.pad ?? 0
    const core = p ? overlapArea(box, { left: o.left + p, top: o.top + p, right: o.right - p, bottom: o.bottom - p }) : a
    // Касание запаса вокруг элемента - в десять раз легче самого элемента.
    area += (core + (a - core) * GAP_SHARE) * o.weight
    if (o.kind === 'task') task += core
    if (o.kind !== 'soft') hard += core * o.weight
  }
  return { task, area, hard }
}

export type ChooseInput = {
  candidates: Spot[]
  obstacles: Obstacle[]
  // Текущее место (null - родное, то есть candidates[0]).
  current: Spot | null
  w: number
  h: number
  vw: number
  vh: number
  phone: boolean
}

// Мелкое касание задания (пара пикселей рамки) не считаем: иначе из-за
// одного пикселя шар уходил бы в середину экрана.
const TASK_EPS = 16

export function chooseFabSpot({ candidates, obstacles, current, w, h, vh, phone }: ChooseInput): Spot {
  const scores = candidates.map((c) => scoreSpot(c, w, h, obstacles))
  const zone = (c: Spot) => zoneOf(c.top, h, vh, phone)

  // 1. Задание. Сначала места у краёв (нижняя и верхняя трети); средняя треть
  // в игре, только если у краёв задание закрыто везде, а там - меньше.
  const edgeIdx = candidates.map((_, i) => i).filter((i) => i === 0 || zone(candidates[i]) !== 2)
  const minEdgeTask = Math.min(...edgeIdx.map((i) => scores[i].task))
  let pool = edgeIdx
  let minTask = minEdgeTask
  if (phone && minEdgeTask > TASK_EPS) {
    const all = candidates.map((_, i) => i)
    const minAll = Math.min(...all.map((i) => scores[i].task))
    if (minAll < minEdgeTask) {
      pool = all
      minTask = minAll
    }
  }
  const limit = Math.max(minTask, TASK_EPS)
  const allowed = new Set(pool.filter((i) => scores[i].task <= limit))

  // 2. Среди допустимых - прежнее правило: наименьшая взвешенная площадь,
  // при близких значениях - более ранний кандидат (родное место), на
  // телефоне левые места (кроме родного) со штрафом: правый край
  // предпочтительнее (пользователь 2026-09-19).
  let bestI = -1
  let bestCost = Infinity
  for (const i of allowed) {
    const c = candidates[i]
    const sidePenalty = phone && i > 0 && c.side === 'left' ? 1.6 : 1
    const cost = scores[i].area * (1 + i * 0.15) * sidePenalty
    if (cost < bestCost) {
      bestI = i
      bestCost = cost
    }
  }
  const best = candidates[bestI]

  // 3. Гистерезис: на телефоне все места чем-то перекрыты, и без него шар
  // менял угол на каждом скролле (пользователь 2026-09-19). Но место,
  // закрывающее задание сильнее лучшего, не держим ни при каком раскладе.
  const cur = current ?? candidates[0]
  const curScore = scoreSpot(cur, w, h, obstacles)
  if (curScore.task > limit) return best
  // Родное место освободилось - возвращаемся туда, гистерезис не держит шар в
  // чужом углу. В уроке на телефоне "Дальше" с 15.10.2026 занимает правую
  // половину низа, и шар должен стоять слева от неё, а не над ней.
  if (cur !== candidates[0] && scores[0].task <= TASK_EPS && scores[0].hard === 0 && scores[0].area <= w * h * 0.05) return candidates[0]
  // Средняя треть - только пока у краёв нет места без задания.
  if (phone && zone(cur) === 2 && cur !== candidates[0] && pool === edgeIdx) return best
  // Низ важнее гистерезиса: шар, уехавший под шапку, возвращается в нижнюю
  // треть, как только лучшее место там не хуже текущего (каталог, 2026-10-03).
  if (phone && zone(cur) !== 0) {
    let lowI = -1
    let lowArea = Infinity
    for (const i of allowed) {
      const c = candidates[i]
      if (zone(c) !== 0 || c.side === 'left') continue
      if (scores[i].area < lowArea) {
        lowI = i
        lowArea = scores[i].area
      }
    }
    if (lowI >= 0 && lowArea <= curScore.area) return candidates[lowI]
  }
  // Отдельно по кнопкам и ссылкам: под шаром пилюля, а рядом только
  // карточка-подложка - уходим, даже если общая площадь почти равна.
  const moveByHard = curScore.hard > 0 && scores[bestI].hard <= curScore.hard * 0.5
  // Почти свободное место (касание запаса, край строки) считаем свободным:
  // иначе шар из полосы под кнопкой шага уезжал под шапку.
  const nearlyFree = curScore.area <= w * h * 0.05
  if (!moveByHard && (nearlyFree || scores[bestI].area > curScore.area * 0.5)) return cur
  return best
}
